import clsx from 'clsx';
import debounce from 'lodash/debounce';
import { MutableRefObject, RefObject, useEffect, useRef, useState } from 'react';
import { Column, Row, useBlockLayout, useTable } from 'react-table';

interface Props<T extends object> {
	data: T[];
	columns: Readonly<Column<T>[]>;
	containerClassName?: string;
	tableClassName?: string;
	onSelect?: (selectedRows: any) => void;
	onRowDoubleClick?: (row: Row<T>) => void;
	selectedRowIndex?: number;
	isLoading?: boolean;
	onLoadMore?: () => void;
	stopLoad?: boolean;
	resetScrollFn: MutableRefObject<any>;
}

function TableSelect<T extends object>({
	data,
	containerClassName,
	tableClassName,
	onSelect,
	onRowDoubleClick,
	columns: tableColumns,
	selectedRowIndex,
	isLoading,
	onLoadMore,
	stopLoad,
	resetScrollFn,
}: Readonly<Props<T>>) {
	const [tableData, setTableData] = useState<T[]>([]);

	const tableRef = useRef<HTMLTableElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const { rows, getTableProps, getTableBodyProps, headerGroups, prepareRow, columns } =
		useTable<T>(
			{
				columns: tableColumns,
				data: tableData,
			},
			useBlockLayout
		);

	useEffect(() => {
		if(resetScrollFn && containerRef.current) {
			resetScrollFn.current = () => {
				containerRef.current!.scrollTop = 0;
			}
		}
	}, [containerRef]);
	
	useEffect(() => {
		setTableData(data);
	}, [data]);

	useEffect(() => {
		const isValid = selectedRowIndex !== undefined && selectedRowIndex >= 0 && tableRef.current;

		if (isValid) {
			const rows = tableRef.current.rows;
			const selectedRow = Array.from(rows).find((row) =>
				row.classList.contains('selected-row')
			);

			if (!selectedRow) return;
			const selectedRowDimension = selectedRow.getBoundingClientRect();
			const tableDimension = tableRef.current.getBoundingClientRect();

			if (
				selectedRowDimension.top > tableDimension.height ||
				selectedRowDimension.top > tableDimension.top
			) {
				selectedRow?.scrollIntoView({
					behavior: 'smooth',
					block: 'end',
				});
			}
		}
	}, [selectedRowIndex]);

	const MessageRow = ({ message }: { message?: string }) => {
		return (
			<tr>
				<td colSpan={columns.length} className="p-2">
					{message}
				</td>
			</tr>
		);
	};

	const TableRow = ({ row }: { row: Row<T> }) => {
		return (
			<tr
				{...row.getRowProps()}
				className={clsx('cursor-pointer', {
					'selected-row': row.index === selectedRowIndex,
				})}
				onClick={() => {
					onSelect && onSelect(row.original);
				}}
				onDoubleClick={() => {
					onRowDoubleClick && onRowDoubleClick(row);
				}}
			>
				{row.cells.map((cell) => {
					return (
						<td
							title={cell.value}
							className={clsx('align-middle spaces py-4 px-8 text-truncate')}
							{...cell.getCellProps()}
						>
							{cell.render('Cell')}
						</td>
					);
				})}
			</tr>
		);
	};

	const onScroll = () => {
		const container = containerRef.current;
		
		if (!container || isLoading || stopLoad) return;

		const ROW_OFFSET_HEIGHT = Math.floor(container.scrollHeight / 4);
		const scrollEnd = container.scrollHeight - container.scrollTop - container.clientHeight;

		if (scrollEnd < ROW_OFFSET_HEIGHT) {
			onLoadMore?.();
		}
	};
	const debounceScroll = debounce(onScroll, 200);

	useEffect(() => {
		const container = containerRef.current;

		if (!container) return;

		container.addEventListener('scroll', debounceScroll);

		return () => {
			container.removeEventListener('scroll', debounceScroll);
		};
	}, [isLoading, stopLoad, debounceScroll]);

	return (
		<div className={clsx('table-responsive w-100', containerClassName)} ref={containerRef}>
			<table
				ref={tableRef}
				id="kt_table_custom"
				className={clsx('align-middle table-bordered w-100', tableClassName)}
				{...getTableProps()}
			>
				<thead className="position-sticky top-0 z-index-1">
					{headerGroups.map((headerGroup) => (
						<tr
							className="text-start border fw-bolder fs-8 gs-0 border bg-pri border-y-none "
							{...headerGroup.getHeaderGroupProps()}
						>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()} className="p-2 text-center">
									{column.render('Header')}
								</th>
							))}
						</tr>
					))}
				</thead>

				<tbody className="text-gray bg-white border" {...getTableBodyProps()}>
					{!rows.length && !isLoading && (
						<MessageRow message='Không có dữ liệu.' />
					)}
					{rows.map((row, i) => {
						prepareRow(row);
						return <TableRow key={row.id} row={row} />;
					})}
					{isLoading && <MessageRow message='Đang tải dữ liệu ...' />}
				</tbody>
			</table>
		</div>
	);
}

export default TableSelect;
